import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress, IconButton, Stack } from "@mui/material";
import {
  AnchorNav,
  Button,
  DrawerContent,
  DrawerNavBar,
  Icon,
  Typography,
  type DialogComponentProps,
} from "@platform/design-system";
import { useMemo, useState } from "react";

import { useCopyTextToClipboard } from "../../../../../hooks/useCopyTextToClipboard";

import CarrierTabBar from "./CarrierTabBar";
import CommentGroupList from "./CommentGroupList";
import { formatForClipboard } from "./formatForClipboard";
import { useNeedsReviewComments } from "./useNeedsReviewComments";

const ecoRfpIdOf = (
  data: Record<string, unknown> | undefined,
): string | undefined =>
  typeof data?.ecoRfpId === "string" ? data.ecoRfpId : undefined;

const NeedsReviewCommentsDrawer = ({
  data,
  onClose,
}: DialogComponentProps): React.JSX.Element => {
  const ecoRfpId = ecoRfpIdOf(data);
  const { carriers, isLoading, optionLabelByProposalPlanId } =
    useNeedsReviewComments({ ecoRfpId });
  const copyToClipboard = useCopyTextToClipboard();

  const [selectedCarrierId, setSelectedCarrierId] = useState<
    string | undefined
  >(undefined);
  const activeCarrierId = selectedCarrierId ?? carriers[0]?.carrierId;
  const activeCarrier = useMemo(
    () => carriers.find((carrier) => carrier.carrierId === activeCarrierId),
    [carriers, activeCarrierId],
  );

  const anchorMenuItems = useMemo(
    () =>
      (activeCarrier?.navEntries ?? []).map((entry) => ({
        id: entry.key,
        label: entry.label,
      })),
    [activeCarrier],
  );

  const handleCopyAll = async (): Promise<void> => {
    if (!activeCarrier) return;
    const { html, text } = formatForClipboard(
      activeCarrier,
      optionLabelByProposalPlanId,
    );
    await copyToClipboard(
      text,
      `Copied ${activeCarrier.carrierName}'s comments.`,
      "Failed to copy comments. Please try again.",
      { html },
    );
  };

  return (
    <>
      <DrawerNavBar sx={{ borderBottom: 0 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <Typography variant="subtitle1" color="textSecondary">
            Needs Review Comments
          </Typography>
          <IconButton
            onClick={() => onClose()}
            aria-label="Close drawer"
            data-testid="needs-review-comments-close-icon"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DrawerNavBar>

      {isLoading ? (
        <DrawerContent>
          <Stack alignItems="center" justifyContent="center" flex={1}>
            <CircularProgress />
          </Stack>
        </DrawerContent>
      ) : carriers.length === 0 || !activeCarrier ? (
        <DrawerContent>
          <Stack alignItems="center" justifyContent="center" flex={1}>
            <Typography color="textSecondary">
              No Needs Review comments have been posted for this RFP.
            </Typography>
          </Stack>
        </DrawerContent>
      ) : (
        <>
          <CarrierTabBar
            carriers={carriers}
            activeCarrierId={activeCarrier.carrierId}
            onChange={setSelectedCarrierId}
          />
          <DrawerContent sx={{ p: 0, bgcolor: "grey.100" }}>
            <AnchorNav
              anchorMenuItems={anchorMenuItems}
              menuPosition="right"
              initialActiveId={anchorMenuItems[0]?.id}
              key={activeCarrier.carrierId}
              AnchorMenuListSxProps={{
                bgcolor: "common.white",
                height: "100%",
              }}
              AnchorMenuListBoxSxProps={{
                alignSelf: "stretch",
              }}
            >
              {({ scrollContainerRef, setSectionRef }) => (
                <Stack
                  ref={scrollContainerRef}
                  flex={1}
                  sx={{ overflow: "auto", minHeight: 0 }}
                >
                  <CommentGroupList
                    carrier={activeCarrier}
                    setSectionRef={setSectionRef}
                  />
                </Stack>
              )}
            </AnchorNav>
          </DrawerContent>
        </>
      )}

      <DrawerNavBar bottom>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          alignItems="center"
          width="100%"
        >
          <Button variant="tertiary" onClick={() => onClose()}>
            Close
          </Button>
          <Button
            variant="primary"
            startIcon={<Icon name="copy" />}
            onClick={handleCopyAll}
            disabled={!activeCarrier}
            data-testid="needs-review-comments-copy-all"
          >
            Copy All
          </Button>
        </Stack>
      </DrawerNavBar>
    </>
  );
};

export default NeedsReviewCommentsDrawer;
